import React, { useEffect, useState } from 'react';

interface Props {
  target: HTMLElement | null;
  padding?: number;
  borderRadius?: number;
  onBboxChange?: (bbox: DOMRect | null) => void;
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

function bboxOf(el: HTMLElement | null, padding: number): Box | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - padding,
    left: r.left - padding,
    width: r.width + padding * 2,
    height: r.height + padding * 2,
  };
}

export function TutorialSpotlight({
  target,
  padding = 8,
  borderRadius = 14,
  onBboxChange,
}: Props) {
  const [box, setBox] = useState<Box | null>(() => bboxOf(target, padding));

  useEffect(() => {
    if (!target) {
      setBox(null);
      onBboxChange?.(null);
      return;
    }

    const recompute = () => {
      const next = bboxOf(target, padding);
      setBox(next);
      onBboxChange?.(target.getBoundingClientRect());
    };

    recompute();

    const ro = new ResizeObserver(recompute);
    ro.observe(target);
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);

    let raf = 0;
    const loop = () => {
      recompute();
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);

    const stopRaf = window.setTimeout(() => {
      cancelAnimationFrame(raf);
      raf = 0;
    }, 600);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(stopRaf);
    };
  }, [target, padding, onBboxChange]);

  if (!box) {
    return (
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{ background: 'rgba(8, 18, 13, 0.78)', zIndex: 60 }}
      />
    );
  }

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        top: box.top,
        left: box.left,
        width: box.width,
        height: box.height,
        borderRadius,
        boxShadow: '0 0 0 9999px rgba(8, 18, 13, 0.78)',
        pointerEvents: 'none',
        transition: 'top 220ms ease, left 220ms ease, width 220ms ease, height 220ms ease',
        zIndex: 60,
      }}
    />
  );
}
